import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  CircularProgress,
  ClickAwayListener,
  FilledInput,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import axios from 'axios';

import { debounce, noop } from '@altirllc/shared/utils/functionalUtils';

import { handleError } from '@/utils/errorUtils';

import type { TEntityOption } from '@/components/filter/types';
import { ChevronDown } from '@/components/icons/ChevronDown';
import { Search } from '@/components/icons/Search';
import { Loader } from '@/components/Loader';

import { styles } from './UploadCatalog.styles';

type TUploadCatalogStationSelectProps = {
  value: TEntityOption | null;
  getOptions: (query: string, signal: AbortSignal) => Promise<TEntityOption[]>;
  onChange: (station: TEntityOption | null) => void;
  disabled?: boolean;
  /**
   * Read-only site: shows `value` without a dropdown chevron and never opens the list, so
   * `onChange` cannot fire. Used when the popup opens from a station's Products tab.
   */
  locked?: boolean;
  /**
   * The preselected site is still being fetched. Shows a spinner where the site name will
   * appear and marks the field `aria-busy`. Ignored once a `value` is present. It never blocks
   * clicks: an unlocked field stays usable, a locked one stays read-only.
   */
  loading?: boolean;
};

/**
 * Site field of the Upload Catalog popup: a trigger that opens a searchable list of sites.
 * With `locked` it only displays the given site (read-only). With `loading` it shows a spinner
 * in the value row (same height as the name, so nothing jumps when the name arrives).
 */
export const UploadCatalogStationSelect = ({
  value,
  getOptions,
  onChange,
  disabled,
  locked = false,
  loading: restoring = false,
}: TUploadCatalogStationSelectProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<TEntityOption[]>([]);
  const [loading, setLoading] = useState(false);
  const showSpinner = restoring && !value;
  const abort = useRef<AbortController | null>(null);

  const search = useCallback(
    async (query: string) => {
      if (abort.current) {
        abort.current.abort();
      }

      setLoading(true);

      try {
        // AbortController is used across filters; Safari 11 is outside our support matrix.
        // eslint-disable-next-line compat/compat
        abort.current = new AbortController();
        const nextOptions = await getOptions(query, abort.current.signal);
        setOptions(nextOptions);
        setLoading(false);
      } catch (error) {
        handleError(error, 'UNHANDLED_RESPONSE');

        if (!axios.isCancel(error)) {
          setLoading(false);
          setOptions([]);
        }
      }
    },
    [getOptions],
  );

  const searchDebounced = useMemo(() => debounce(search, 200), [search]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    search('').then(noop);

    return () => {
      abort.current?.abort();
    };
  }, [open, search]);

  const handleClose = () => setOpen(false);

  const handleSelect = (station: TEntityOption) => {
    onChange(station);
    handleClose();
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={styles.stationSelect} aria-busy={showSpinner}>
        <Box
          sx={{ ...styles.stationTrigger, ...(locked ? styles.stationTriggerLocked : null) }}
          onClick={() => {
            if (!disabled && !locked) {
              setOpen((prev) => !prev);
            }
          }}
        >
          <Typography sx={styles.stationLabel}>
            {t('productCatalog.import.selectStationLabel')}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1} sx={styles.stationValueRow}>
            {showSpinner ? (
              <Box sx={styles.stationValueSpinner}>
                <CircularProgress size={18} sx={styles.stationSpinner} />
              </Box>
            ) : (
              <Typography sx={styles.stationValue} noWrap>
                {value?.title ?? ''}
              </Typography>
            )}
            {!locked && (
              <ChevronDown
                sx={{
                  ...styles.stationChevron,
                  transform: open ? 'rotate(180deg)' : 'none',
                }}
              />
            )}
          </Stack>
        </Box>

        {open && (
          <Box sx={styles.stationDropdown}>
            <Box sx={styles.stationSearch}>
              <FilledInput
                size="small"
                margin="none"
                placeholder={t('filtering.searchBySiteName')}
                onChange={({ currentTarget }) => searchDebounced(currentTarget.value)}
                startAdornment={
                  <InputAdornment position="start" sx={styles.stationSearchIcon}>
                    <Search fontSize="inherit" />
                  </InputAdornment>
                }
                autoFocus
                fullWidth
              />
            </Box>

            <Box sx={styles.stationOptions}>
              <Loader active={loading} position="absolute" />

              {options.map((option) => {
                const selected = value?.id === option.id;

                return (
                  <Box
                    key={option.id}
                    onClick={() => handleSelect(option)}
                    sx={{
                      ...styles.stationOption,
                      ...(selected ? styles.stationOptionSelected : null),
                    }}
                  >
                    <Typography sx={styles.stationOptionTitle}>{option.title}</Typography>
                    {option.details && (
                      <Typography sx={styles.stationOptionDetails}>{option.details}</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};
